import { SupportedHostAppSchema } from 'renderer/features/products-page/schemas/products.schemas';
import { PricesSchema } from 'renderer/features/purchase-page/schemas/prices.schema';
import { z } from 'zod';

// Detail
const BaseTransactionSchema = z.object({
  _id: z.string(),
  email: z.string().optional(),
  userId: z.string().optional(),
  // stripeId: z.string().optional(),
  amount: z.number(),
  status: z.string(),
  createdAt: z.string(),
  productName: z.string().optional(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  category: z.string().optional(),
  refundedAmount: z.number(),
});

export type BaseTransaction = z.infer<typeof BaseTransactionSchema>;

export const TransactionListSchema = BaseTransactionSchema.array();

const LifecycleCheckpointSchema = z.object({
  step: z.string(),
  label: z.string(),
  status: z.enum(['completed', 'failed']),
  at: z.string(),
  detail: z.string().nullish(),
});

const EmbeddedBillingAddressSchema = z.object({
  nameOnCard: z.string().nullish(),
  countryName: z.string().nullish(),
  streetAddress: z.string().nullish(),
  locality: z.string().nullish(),
  region: z.string().nullish(),
  postalCode: z.string().nullish(),
});

const EmbeddedPaymentMethodSchema = z.object({
  _id: z.string().optional(),
  provider: z.string().nullish(),
  stripeId: z.string().nullish(),
  braintreeId: z.string().nullish(),
  cardType: z.string(),
  last4: z.string().nullish(),
  expirationMonth: z.string().nullish(),
  expirationYear: z.string().nullish(),
  default: z.boolean().nullish(),
  billingAddress: EmbeddedBillingAddressSchema.nullish(),
});

const SerialGroupSchema = z.object({
  productId: z.string(),
  productName: z.string(),
  serials: z.array(z.string()),
  version: z.string().optional(),
  displayName: z.string().optional(),
});

export const TransactionDetailsSchema = BaseTransactionSchema.extend({
  embeddedPaymentMethod: EmbeddedPaymentMethodSchema.optional(),
  fullPrice: z.number().optional(),
  prices: PricesSchema.optional(),
  originalSerial: z.string().optional(),
  paymentMethodId: z.string().optional(),
  platform: z.string().optional(),
  hostApplication: SupportedHostAppSchema.optional(),
  productId: z.string().optional(),
  // productId: ProductDetailsSchema.partial().or(z.string()).optional(),
  quantity: z.number().optional(),
  serials: z.string().array(),
  serialGroups: z.array(SerialGroupSchema).optional(),
  updatedAt: z.string().optional(),
  // refunds: RefundListSchema.optional(),
  // userId: UserDetailsSchema.optional(),
  upgradeOrDowngrade: z.string().optional(),
  note: z.string().optional(),
  cartId: z.string().optional(),
  userId: z.string().optional(),
  billingReason: z.string().optional(),
  stripeId: z.string().nullish(),
  paypalId: z.string().nullish(),
  braintreeTransactionId: z.string().nullish(),
  processor: z.string().nullish(),
  paymentProvider: z.string().nullish(),
  providerDashboardUrl: z.string().url().nullish(),
  processingStartedAt: z.string().optional(),
  completedAt: z.string().optional(),
  failedAt: z.string().optional(),
  metadata: z
    .object({
      stage: z.string().nullish(),
      errorMessage: z.string().nullish(),
      errorCode: z.string().nullish(),
      errorStack: z.string().nullish(),
      failureDetails: z
        .object({
          summary: z.string(),
          provider: z.enum(['stripe', 'braintree', 'paypal']).nullish(),
          failurePoint: z.string().nullish(),
          processorCode: z.string().nullish(),
          processorMessage: z.string().nullish(),
          declineType: z.string().nullish(),
          errorCode: z.string().nullish(),
          declineCode: z.string().nullish(),
          adviceCode: z.string().nullish(),
          networkDeclineCode: z.string().nullish(),
          requestId: z.string().nullish(),
          requestLogUrl: z.string().url().nullish(),
          gatewayRejectionReason: z.string().nullish(),
          avs: z
            .object({
              street: z.string().nullish(),
              postalCode: z.string().nullish(),
              cvv: z.string().nullish(),
            })
            .nullish(),
          card: z
            .object({
              cardType: z.string().nullish(),
              last4: z.string().nullish(),
              bin: z.string().nullish(),
            })
            .nullish(),
        })
        .nullish(),
      checkoutCart: z
        .object({
          checkoutSessionId: z.string(),
          cartId: z.string().optional(),
          lines: z.array(
            z.object({
              cartLineIndex: z.number(),
              productId: z.string(),
              productName: z.string(),
              category: z.string(),
              quantity: z.number(),
              hostApplication: SupportedHostAppSchema.optional(),
              platform: z.string().optional(),
            }),
          ),
          thisTransaction: z.object({
            cartLineIndex: z.number(),
            copyIndex: z.number(),
            copiesInLine: z.number(),
          }),
        })
        .optional(),
    })
    .nullish(),
  checkoutSessionId: z.string().optional(),
  checkoutSessionTransactions: z
    .array(
      z.object({
        transactionId: z.string(),
        cartLineIndex: z.number(),
        copyIndex: z.number(),
      }),
    )
    .optional(),
  checkpoints: z.array(LifecycleCheckpointSchema).optional(),
});

export const TransactionsSchema = TransactionDetailsSchema.array();

export type GetTransactionDetailResponse = z.infer<
  typeof TransactionDetailsSchema
>;
export type TransactionDetails = GetTransactionDetailResponse;
export type GetTransactionDetailRequest = string;

// List
export const ListTransactionsResponseSchema = z.object({
  docs: TransactionListSchema,
  totalDocs: z.number(),
});

export type ListTransactionsResponse = z.infer<
  typeof ListTransactionsResponseSchema
>;

export interface ListTransactionsRequest {
  page: number;
  limit: number;
  search?: string;
  sortBy?: string;
  sortOrder: string;
  startDate?: string;
  endDate?: string;
  filter?: any;
}

export interface UpdateTransactionNoteRequest {
  transactionId: string;
  note: string;
}

export const UpdateTransactionNoteResponseSchema = TransactionDetailsSchema;
export type UpdateTransactionNoteResponse = TransactionDetails;
